import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loginApi, registerApi, profileApi } from '../api/authApi';
import { TOKEN_KEY, USER_ID_KEY, WALLET_KEY, REFRESH_TOKEN_KEY } from '../constants/config';
import { clearSession, getAccessToken, readWallet, saveSession, saveWallet } from '../utils/session';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // user object localStorage me nahi rakhte - sirf memory me; reload par /user/profile se aata hai
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(readWallet);
  const [accessToken, setAccessToken] = useState(getAccessToken);

  const logout = useCallback(() => {
    clearSession();
    setUser(null);
    setWallet(null);
    setAccessToken(null);
  }, []);

  // API: GET /user/profile -> data.user + data.wallet
  const refreshProfile = useCallback(async () => {
    const res = await profileApi();
    const data = res.data?.data;
    if (data?.user) {
      setUser(data.user);
      if (data.user.userId || data.user._id) {
        try { localStorage.setItem(USER_ID_KEY, String(data.user.userId || data.user._id)); } catch { /* ignore */ }
      }
    }
    if (data?.wallet) {
      setWallet(data.wallet);
      saveWallet(data.wallet);
    }
    return data;
  }, []);

  useEffect(() => {
    const onUnauthorized = () => logout();
    const onStorage = (event) => {
      if (!event.key || [TOKEN_KEY, REFRESH_TOKEN_KEY, WALLET_KEY].includes(event.key)) {
        setAccessToken(getAccessToken());
        setWallet(readWallet());
      }
    };
    window.addEventListener('auth:unauthorized', onUnauthorized);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('auth:unauthorized', onUnauthorized);
      window.removeEventListener('storage', onStorage);
    };
  }, [logout]);

  // page reload par token hai to profile dobara le aao
  useEffect(() => {
    if (accessToken && !user) refreshProfile().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  // API: POST /auth/login
  const login = useCallback(async ({ identifier, password }) => {
    const response = await loginApi({ identifier: identifier.trim(), password });
    const body = response.data;
    if (!body?.success || !body?.data?.accessToken || !body?.data?.user) {
      throw new Error(body?.message || 'Unable to sign in. Please try again.');
    }
    const { accessToken: at, refreshToken, user: u, wallet: w } = body.data;
    saveSession({ accessToken: at, refreshToken, userId: u.userId || u._id, wallet: w });
    setUser(u);
    if (w) setWallet(w);
    setAccessToken(at);
    // login response me wallet na ho to profile se le lo
    if (!w) refreshProfile().catch(() => {});
    return body;
  }, [refreshProfile]);

  // API: POST /auth/register
  const register = useCallback(async ({ username, mobile, password }) => {
    const response = await registerApi({ username: username.trim(), mobile: mobile.trim(), password });
    const body = response.data;
    if (body?.success === false) throw new Error(body.message || 'Registration failed.');
    return body;
  }, []);

  const value = useMemo(
    () => ({ user, wallet, isAuthenticated: Boolean(accessToken), login, register, logout, refreshProfile }),
    [user, wallet, accessToken, login, register, logout, refreshProfile]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
